import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useModelStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import { modelsApi } from '@/lib/api/models';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import ModelSelectorList from './ModelSelectorList';
import { isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';

const normalizeIds = ids => Array.isArray(ids) ? ids : ids ? [ids] : [];
const EMPTY_IDS = [];
const SEARCH_DEBOUNCE_MS = 250;
const NO_SEARCH = { query: '', ids: EMPTY_IDS, loading: false };

// Keep data, access rules and selection in one place for every picker surface.
export default function ModelSelectorDetailed({
  modelIds, onChange, maxModels = 5, inputs, outputs, tags, capabilityPath,
  allowedSlugs, isDisabled = false, onRequestClose, noAutoMode = false,
  noMultiSelectMode = false, restrictMultipleModelsToSameType = false,
}) {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [selectedModelIds, setSelectedModelIds] = useState(() => normalizeIds(modelIds));
  const [isComparing, setIsComparing] = useState(() => !noMultiSelectMode && normalizeIds(modelIds).length > 1);
  const comparingRef = useRef(isComparing);
  const user = useAuthStore(state => state.user);
  const favoriteModelIds = user?.favoriteModelIds || EMPTY_IDS;
  const toggleFavoriteModel = useAuthStore(state => state.toggleFavoriteModel);
  const { addToast } = useToastStore(state => state.actions);
  const [savingFavorites, setSavingFavorites] = useState(new Set());
  const savingFavoritesRef = useRef(new Set());
  const { openModal } = useModalStore(state => state.actions);
  const models = useModelStore(state => state.models);
  const allModelIds = useModelStore(state => state.allModelIds);
  const modelTags = useModelStore(state => state.modelTags);
  const frequentModelIds = useModelStore(state => state.frequentModelIds);
  const isLoading = useModelStore(state => state.isLoadingAllModels);
  const isFetched = useModelStore(state => state.isFetchedAllModels);
  const loadError = useModelStore(state => state.allModelsError);
  const fetchAllModels = useModelStore(state => state.fetchAllModels);
  const fetchFrequentModels = useModelStore(state => state.fetchFrequentModels);
  const fetchModelTags = useModelStore(state => state.fetchModelTags);
  const addModels = useModelStore(state => state.addModels);
  const [serverSearch, setServerSearch] = useState(NO_SEARCH);
  const searchTimerRef = useRef(null);
  const searchRequestRef = useRef(0);

  // The catalog loads page by page; ask the server so matches on unloaded pages appear too.
  const searchServer = useCallback(text => {
    clearTimeout(searchTimerRef.current);
    const request = ++searchRequestRef.current;
    const query = text.trim();
    if (!query) { setServerSearch(NO_SEARCH); return; }
    setServerSearch({ query, ids: EMPTY_IDS, loading: true });
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { models: found } = await modelsApi.search(query, 1);
        if (request !== searchRequestRef.current) return;
        setServerSearch({ query, ids: addModels(found || []).map(model => model.id), loading: false });
      } catch {
        if (request === searchRequestRef.current) setServerSearch({ query, ids: EMPTY_IDS, loading: false });
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [addModels]);
  useEffect(() => () => { clearTimeout(searchTimerRef.current); searchRequestRef.current++; }, []);

  useEffect(() => {
    if (modelIds == null) return;
    const ids = normalizeIds(modelIds);
    setSelectedModelIds(ids);
    if (ids.length > 1 && !noMultiSelectMode) {
      comparingRef.current = true;
      setIsComparing(true);
    }
  }, [modelIds, noMultiSelectMode]);

  useEffect(() => {
    // The store loads subsequent pages itself. Never restart page one between pages.
    const state = useModelStore.getState();
    if (!state.isFetchedAllModels && !state.isLoadingAllModels) fetchAllModels();
    if (!state.isFetchedFrequentModels && !state.isLoadingFrequentModels) fetchFrequentModels();
    if (!Object.keys(state.modelTags).length && !state.isLoadingModelTags && !state.modelTagsError) fetchModelTags();
  }, [fetchAllModels, fetchFrequentModels, fetchModelTags]);
  // Helper function to check if model matches capability requirements from props
  const modelMatchesCapabilities = useCallback((model) => {
    // Check input capabilities
    if (inputs?.length > 0) {
      const hasAllInputs = inputs.every(input => {
        switch (input) {
          case 'text':
            return model.capabilities?.input?.hasTextSupport;
          case 'file':
            return model.capabilities?.input?.hasFileSupport;
          case 'image':
            return model.capabilities?.input?.hasImageSupport;
          case 'audio':
            return model.capabilities?.input?.hasAudioSupport;
          case 'video':
            return model.capabilities?.input?.hasVideoSupport;
          default:
            return false;
        }
      });
      if (!hasAllInputs) return false;
    }

    // Check output capabilities. Note the alias mapping:
    //   - 'image'    → output.hasImages
    //   - 'images'   → output.hasImages          (alias)
    //   - '3d-model' → output.has3D              (matches Tool.SUPPORTED_OUTPUT_TYPES)
    //   - '3d'       → output.has3D              (alias)
    //   - 'music'    → output.hasAudio           (no separate music capability)
    //   - 'code'     → output.hasText            (code is text)
    //   - 'document' → output.hasText            (documents are text)
    // If callers need finer-grained filtering (e.g. only image-to-3D models),
    // pass `capabilityPath` instead — see `Tool.modelFilter.capability`.
    if (outputs?.length > 0) {
      const out = model?.capabilities?.output || {};
      const hasAllOutputs = outputs.every(output => {
        switch (output) {
          case 'text':
          case 'code':
          case 'document':
            return !!out.hasText;
          case 'image':
          case 'images':
            return !!out.hasImages;
          case 'audio':
          case 'music':
            return !!out.hasAudio;
          case 'video':
            return !!out.hasVideo;
          case '3d-model':
          case '3d':
            return !!out.has3D;
          case 'streaming':   return !!out.hasStreaming;
          case 'thinking':    return !!out.hasThinking;
          case 'citations':   return !!out.hasCitations;
          case 'websearch':   return !!out.hasWebSearch;
          case 'functioncalling': return !!out.hasFunctionCalling;
          case 'tooluse':     return !!out.hasToolUse;
          default:
            return false;
        }
      });
      if (!hasAllOutputs) return false;
    }

    // Explicit dotted capability path (mirrors tool.modelFilter.capability).
    // Walks `model[a][b][c]` and only passes if the resolved value is truthy.
    if (capabilityPath) {
      const parts = String(capabilityPath).split('.');
      let cur = model;
      for (const p of parts) {
        if (cur == null) { cur = undefined; break; }
        cur = cur[p];
      }
      if (!cur) return false;
    }

    // Explicit slug allow-list (mirrors tool.modelFilter.slugs).
    if (allowedSlugs?.length > 0) {
      if (!model?.slug || !allowedSlugs.includes(model.slug)) return false;
    }

    // Check tags
    if (tags?.length > 0) {
      const hasAllTags = tags.every(tagId => 
        (model.tags || []).some(modelTag => modelTag?.id === tagId || modelTag?.slug === tagId)
      );
      if (!hasAllTags) return false;
    }

    return true;
  }, [inputs, outputs, capabilityPath, allowedSlugs, tags]);

  const fullCatalog = useMemo(() => {
    const ids = [...new Set([...allModelIds, ...selectedModelIds, ...favoriteModelIds, ...frequentModelIds, ...serverSearch.ids])];
    return ids.map(id => models[id]).filter(model => model && modelMatchesCapabilities(model));
  }, [allModelIds, selectedModelIds, favoriteModelIds, frequentModelIds, serverSearch.ids, models, modelMatchesCapabilities]);
  const selectedModels = useMemo(() => selectedModelIds.map(id => models[id]).filter(Boolean), [selectedModelIds, models]);

  const isUnavailable = useCallback(model => {
    if (isDisabled) return true;
    if (!isComparing || selectedModelIds.includes(model.id)) return false;
    if (selectedModelIds.length >= maxModels) return true;
    if (!restrictMultipleModelsToSameType || !selectedModels.length) return false;
    const first = selectedModels[0].capabilities || {};
    const next = model.capabilities || {};
    const commonInput = ['hasTextSupport', 'hasImageSupport', 'hasAudioSupport', 'hasVideoSupport']
      .some(key => first.input?.[key] && next.input?.[key]);
    const commonOutput = ['hasText', 'hasImages', 'hasAudio', 'hasVideo', 'has3D']
      .some(key => first.output?.[key] && next.output?.[key]);
    return !commonInput || !commonOutput;
  }, [isDisabled, isComparing, selectedModelIds, maxModels, restrictMultipleModelsToSameType, selectedModels]);

  const commitSelection = ids => {
    setSelectedModelIds(ids);
    onChange?.(ids);
  };
  const selectModel = model => {
    if (isUnavailable(model)) return;
    if (isModelLockedForUser(user, model)) {
      onRequestClose?.();
      const isVideo = !!model.capabilities?.output?.hasVideo;
      openModelAccessPaywall(openModal, {
        model,
        title: t(`nav.modelSelectorDetailed.${isVideo ? 'videoModelsPlus' : 'unlockAllModels'}`),
        subtitle: t(`nav.modelSelectorDetailed.${isVideo ? 'videoModelsDesc' : 'unlockAllDesc'}`),
      });
      return;
    }
    if (!comparingRef.current || noMultiSelectMode) {
      // Choosing the current model should keep it selected, not silently switch to Auto.
      commitSelection([model.id]);
      onRequestClose?.();
      return;
    }
    if (selectedModelIds.includes(model.id)) {
      if (noAutoMode && selectedModelIds.length === 1) return;
      commitSelection(selectedModelIds.filter(id => id !== model.id));
    } else if (selectedModelIds.length < maxModels) {
      commitSelection([...selectedModelIds, model.id]);
    }
  };
  const toggleCompare = () => {
    if (isDisabled) return;
    if (!isComparing && !user?.flags?.isPlus) {
      openModal('PlusFeatureModal', { feature: 'compare' });
      return;
    }
    comparingRef.current = !isComparing;
    setIsComparing(!isComparing);
    if (isComparing && selectedModelIds.length > 1) commitSelection(selectedModelIds.slice(0, 1));
  };
  const toggleFavorite = async model => {
    if (isDisabled || savingFavoritesRef.current.has(model.id)) return;
    if (!user) {
      onRequestClose?.();
      openModal('LoginModal');
      return;
    }
    savingFavoritesRef.current.add(model.id);
    setSavingFavorites(new Set(savingFavoritesRef.current));
    try {
      const success = await toggleFavoriteModel(model.id);
      if (!success) throw new Error('Favorite was not saved');
    } catch {
      addToast(t(`modals.modelDetails.${favoriteModelIds.includes(model.id) ? 'failedToUnsave' : 'failedToSave'}`), 'error');
    } finally {
      savingFavoritesRef.current.delete(model.id);
      setSavingFavorites(new Set(savingFavoritesRef.current));
    }
  };
  const retryLoad = () => {
    // A failed page sets isFetchedAllModels, so clear it before a deliberate retry.
    useModelStore.setState({ isFetchedAllModels: false, allModelsError: null });
    fetchAllModels();
  };

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      <ModelSelectorList
        models={fullCatalog} modelTags={modelTags}
        favoriteModelIds={favoriteModelIds} onFavorite={toggleFavorite} savingFavorites={savingFavorites} selectedModelIds={selectedModelIds}
        selectedModels={selectedModels} isComparing={isComparing} maxModels={maxModels}
        onToggleCompare={toggleCompare} onSelect={selectModel}
        onClose={onRequestClose} onAuto={() => { if (!isDisabled) { commitSelection([]); onRequestClose?.(); } }}
        onRemove={id => {
          if (!isDisabled && !(noAutoMode && selectedModelIds.length === 1)) {
            commitSelection(selectedModelIds.filter(value => value !== id));
          }
        }}
        isUnavailable={isUnavailable} isLocked={model => isModelLockedForUser(user, model)}
        isDisabled={isDisabled} noAutoMode={noAutoMode}
        noMultiSelectMode={noMultiSelectMode || maxModels <= 1}
        isLoading={isLoading || !isFetched} loadError={loadError} onRetry={retryLoad}
        onSearchChange={searchServer} serverMatchIds={serverSearch.ids} isSearching={serverSearch.loading}
        isMobile={isXS} bottomInset={insets.bottom}
      />
    </View>
  );
}
